import React, { useState, useMemo, useEffect } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import { feature } from "topojson-client";
import indiaTopo from "../../JSON/india.json";
import { formatRupees } from "../../Utils/common";

const formatRupeeTablueFilterOptions = ["revenue"];

const stateColorOverrides = {
  client: {
    Maharashtra: "#ff5722",
    Delhi: "#009688",
  },
  industry: {
    Maharashtra: "#3f51b5",
    Gujarat: "#8bc34a",
    Delhi: "#009687",
  },
  revenue: {
    Maharashtra: "#ff9800",
    Delhi: "#9c27b0",
  },
  clientType: {
    Karnataka: "#4caf50",
    TamilNadu: "#e91e63",
  },
  subscriptionType: {
    Rajasthan: "#03a9f4",
    Kerala: "#cddc39",
  },
  null: {
    delhi: "#ffc107",
  },
};

const GeographicalCharts = ({ heatMap, tablueFilter }) => {
  const [data, setData] = useState([]);
  useEffect(() => {
    setData(heatMap);
  }, [heatMap]);

  const maxValue = useMemo(() => {
    if (!data || data.length === 0) return 1000;
    return Math.max(
      ...data.flatMap((d) =>
        Object.values(d.values)
          .map(Number)
          .filter((n) => !isNaN(n))
      )
    );
  }, [data]);

  const colorScale = useMemo(() => scaleLinear().domain([0, maxValue]).range(["#fcff36", "#c92c04"]), [maxValue]);

  const statesGeoJson = useMemo(() => feature(indiaTopo, indiaTopo.objects.states), []);

  const [filter, setFilter] = useState("");
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const filteredGeographies = statesGeoJson?.features?.filter((geo) =>
    geo.properties.st_nm?.toLowerCase()?.includes(filter?.toLowerCase())
  );

  const handleMouseEnter = (geo, evt) => {
    const stateName = geo.properties.st_nm;
    const match = data?.find((d) => d?.id?.toLowerCase() === stateName?.toLowerCase());

    if (match) {
      const contentLines = Object.entries(match.values)
        .map(
          ([key, value]) =>
            `<strong style="font-style:italic; font-size:12px ;font-weight:Semi-bold; text-transform:capitalize;">${key} : <span font-size:17px>${
              formatRupeeTablueFilterOptions?.includes(tablueFilter.filterName) ? formatRupees(value) : value
            }</span></strong>`
        )
        .join("<br/>");

      const content = `<strong style="font-size:20px ;font-style:italic" >${stateName}</strong><br/>${contentLines}`;
      setTooltipContent(content);
    } else {
      setTooltipContent(`<strong style="font-size:12px">${stateName}</strong>`);
    }
    setTooltipPosition({ x: evt.clientX + 10, y: evt.clientY + 10 });
  };

  const handleMouseLeave = () => {
    setTooltipContent("");
  };

  const getColor = (geo) => {
    const stateName = geo.properties?.st_nm;
    const match = data?.find((d) => d?.id?.toLowerCase() === stateName?.toLowerCase());
    if (!match) return "#ffffff";

    const numericValues = Object.values(match.values)
      .map((v) => Number(v))
      .filter((n) => !isNaN(n));

    if (numericValues.length === 0) return "#EEE";

    const valueForColor = Math.max(...numericValues);
    return colorScale(valueForColor);
  };

  return (
    <>
      <ComposableMap
        style={{ width: "100%", height: "auto" }}
        projectionConfig={{
          rotate: [-10, 10, 1],
          scale: 110,
        }}
      >
        <ZoomableGroup center={[81, 25]} zoom={8} minZoom={1} maxZoom={50}>
          <Geographies geography={{ type: "FeatureCollection", features: filteredGeographies }}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={getColor(geo)}
                  stroke="#555"
                  strokeWidth={0.2}
                  onMouseEnter={(evt) => handleMouseEnter(geo, evt)}
                  onMouseMove={(evt) => setTooltipPosition({ x: evt.clientX + 10, y: evt.clientY + 10 })}
                  onMouseLeave={handleMouseLeave}
                  style={{
                    default: { outline: "none" },
                    hover: { fill: "#F53", outline: "none", cursor: "pointer" },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {tooltipContent && (
        <div
          style={{
            position: "fixed",
            top: tooltipPosition.y,
            left: tooltipPosition.x,
            background: "white",
            padding: "5px 10px",
            border: "1px solid #CCC",
            borderRadius: "4px",
            pointerEvents: "none",
            fontSize: "0.85rem",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            zIndex: 1000,
          }}
          dangerouslySetInnerHTML={{ __html: tooltipContent }}
        />
      )}
    </>
  );
};

export default GeographicalCharts;
